import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Footer } from "@/components/Footer";
import {
  Clock,
  CheckSquare,
  Calendar,
  BarChart3,
  BookOpen,
  Shield,
  Sparkles,
  Play,
  ArrowRight,
  LogIn,
  Cloud,
} from "lucide-react";
import { SeoHead } from "@/components/SeoHead";

const FEATURES = [
  {
    icon: Clock,
    title: "Supreme Focus Timer",
    desc: "15+ presets from 5-minute bursts to 4-hour deep work blocks",
  },
  {
    icon: CheckSquare,
    title: "Intelligent Task Manager",
    desc: "Category-based organization with priority levels and progress tracking",
  },
  {
    icon: Calendar,
    title: "Visual Calendar",
    desc: "Month/week/day views with activity markers and session tracking",
  },
  {
    icon: BarChart3,
    title: "Deep Analytics",
    desc: "Streak tracking, completion rates, category breakdowns, and trend analysis",
  },
  {
    icon: BookOpen,
    title: "Structured Journaling",
    desc: "Daily, weekly, monthly, and quarterly reflections",
  },
  {
    icon: Shield,
    title: "Full Admin Control",
    desc: "Customize categories, presets, schedules, and weekly rotations",
  },
];

const INCLUDED = [
  "Lifetime access — no subscriptions, no recurring fees",
  "All current and future features included",
  "Works on any device with a browser — desktop, tablet, phone",
  "Installs as a native app (PWA) for offline use",
  "Your data stays 100% local and private on your device",
  "Export/import backups anytime",
  "Full admin customization of every setting",
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleBuy = () => navigate(user ? "/paywall" : "/auth?next=/paywall");
  const handleDemo = () => {
    localStorage.setItem("empire_demo_start", String(Date.now()));
    navigate("/app");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SeoHead
        title="JT Empire OS — Command Center for Operators"
        description="All-in-one local-first command center: ventures, rituals, journals, AI tasks. Works offline. Optional Cloud sync."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "JT Empire OS",
          applicationCategory: "ProductivityApplication",
          operatingSystem: "Web, iOS, Android, Windows, macOS",
          offers: {
            "@type": "Offer",
            price: "97",
            priceCurrency: "USD",
          },
          publisher: { "@type": "Organization", name: "Juxtaposed Tides Media" },
        }}
      />
      {/* Top bar */}
      <div className="max-w-3xl mx-auto w-full px-4 pt-4 flex justify-between items-center gap-2">
        <Link to="/pricing" className="text-xs text-muted-foreground hover:text-primary">Pricing</Link>
        <div className="flex items-center gap-2">
        {user ? (
          <button
            onClick={() => navigate("/app")}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            Open app <ArrowRight className="w-3 h-3" />
          </button>
        ) : (
          <>
            <button
              onClick={() => navigate("/auth")}
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 px-3 py-1.5"
            >
              <LogIn className="w-3 h-3" /> Sign in
            </button>
            <button
              onClick={() => navigate("/auth?mode=signup&next=/paywall")}
              className="text-xs bg-primary text-primary-foreground font-bold hover:opacity-90 px-3 py-1.5 rounded-md"
            >
              Sign up & checkout
            </button>
          </>
        )}
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-10 flex-1">
        {/* Hero */}
        <section className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/15 border border-primary/30">
            <span className="text-3xl">👑</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight">Empire OS — Personal Command Center for Operators</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Your personal command center for managing work, focus, and growth. One purchase. Yours forever.
          </p>
        </section>

        {/* Feature grid */}
        <section className="grid sm:grid-cols-2 gap-3" aria-labelledby="features-heading">
          <h2 id="features-heading" className="sr-only">Features</h2>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="bg-card border border-border rounded-2xl p-4 flex gap-3"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-bold text-sm">{title}</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </section>

        {/* What's included */}
        <section className="bg-card border border-border rounded-2xl p-5">
          <h2 className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
            What's included
          </h2>
          <ul className="space-y-2">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm">
                <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Price + CTA */}
        <section className="text-center space-y-5">
          <div className="flex items-end justify-center gap-2">
            <span className="text-6xl font-black text-primary leading-none">$97</span>
            <span className="text-sm text-muted-foreground mb-2">one-time</span>
          </div>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            One-time license for the software. Optional <Link to="/pricing" className="text-primary underline">Cloud add-ons</Link> from $9/mo unlock cross-device sync, bigger caps, and storage.
          </p>

          <div className="flex flex-col items-center gap-3 pt-2">
            <button
              onClick={handleBuy}
              className="w-full max-w-xs bg-primary text-primary-foreground font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              Get JT EOS <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleDemo}
              className="w-full max-w-xs border border-primary/40 text-primary font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-primary/10 transition-colors"
            >
              <Play className="w-4 h-4" /> Try 4-Minute Live Demo
            </button>
            <Link to="/pricing" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
              <Cloud className="w-3 h-3" /> See all plans &amp; cloud add-ons
            </Link>
          </div>

          <p className="text-xs text-muted-foreground pt-2">
            Secure checkout powered by Stripe. Cloud subscriptions auto-renew &mdash; cancel anytime.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
